import { resolveLabelBarcode } from 'util/label-barcode-resolve';

/** Human-readable litre label for Shopify variants (fallback: unitSize key `'20'`). */
export function kegLitresLabelFromUnitMl(unitMl) {
  if (unitMl == null || unitMl === '') return null;
  const n = parseInt(String(unitMl), 10);
  if (Number.isNaN(n)) return null;
  const exact = {
    19500: '19.5',
    20000: '20',
    30000: '30',
    50000: '50',
  };
  return exact[n] ?? String(n / 1000).replace(/\.0+$/, '');
}

/** @param {string} ut — Matrix unit_type ml for kegs */
export function canonicalKegShopifyPackKeyFromUnitType(ut) {
  switch (String(ut)) {
    case '19500':
    case '20000':
      return '20';
    case '30000':
      return '30';
    case '50000':
      return '50';
    default:
      return null;
  }
}

/**
 * Map a single-unit product_sku row to DTC Shopify builder pack config.
 * Only packType "1" (single) is supported for the base variant barcode.
 *
 * @param {object} sku — camelCase row from API
 * @returns {{ unitSize: string, packType: 'Can'|'Bottle'|'Keg', caseType?: string, caseCount?: number|null, unitMl?: string}|null}
 */
export function inferDtcPackFromSku(sku) {
  if (!sku || sku.deleted || sku.isLegacy) return null;
  const pt = sku.packType == null || sku.packType === '' ? '1' : String(sku.packType);
  if (pt !== '1') return null;
  const caseType = sku.caseType == null ? '' : String(sku.caseType);
  const caseTypeMatch = caseType.match(/^(\d+)x1$/);
  const caseCount = caseTypeMatch ? parseInt(caseTypeMatch[1], 10) : null;
  const ct = sku.containerType;
  const ut = String(sku.unitType ?? '');
  if (ct === 'keg') {
    const canon = canonicalKegShopifyPackKeyFromUnitType(ut);
    if (!canon) return null;
    return {
      unitSize: canon,
      packType: 'Keg',
      caseType,
      caseCount,
      unitMl: ut,
    };
  }
  if (ct === 'can') {
    if (ut === '330' || ut === '440' || ut === '375') {
      return {
        unitSize: ut,
        packType: 'Can',
        caseType,
        caseCount,
      };
    }
    return null;
  }
  if (ct === 'bottle') {
    if (ut === '375' || ut === '750') {
      return {
        unitSize: ut,
        packType: 'Bottle',
        caseType,
        caseCount,
      };
    }
    return null;
  }
  return null;
}

/**
 * Unit barcode for Shopify single-variant: legacy garage_project column first, then SKU bn_unit via shared mapping.
 *
 * @param {object} product — camelCase product row, optional skus[]
 * @param {string} unitSize — can/bottle ml, or keg litres key `'20'|'30'|'50'`
 * @param {'Can'|'Bottle'|'Keg'} packType
 * @param {object} [sku] — selected SKU row; bnUnit fallback for kegs (e.g. 19.5L)
 * @returns {string|null}
 */
export function resolveShopifyUnitBarcode(product, unitSize, packType, sku) {
  if (packType === 'Keg') {
    const fromResolver = resolveLabelBarcode(product, { labelType: 'keg', unitSize });
    if (fromResolver) return fromResolver;
    const bu = sku?.bnUnit ?? sku?.bn_unit;
    if (bu != null && String(bu).trim() !== '') return String(bu).trim();
    return null;
  }
  const labelType = packType === 'Can' ? 'base_can' : 'base_bottle';
  return resolveLabelBarcode(product, { labelType, unitSize });
}
